import { Columns, ConditionalContent } from '@domain-group/suit-theme'

import { digObject, generateRedirectUrl } from '@domain-group/javascript-utils'

import useAgency from '@hooks/useAgency'
import { generateUrls } from '@hooks/useBrand'

import ApiKeys from '@components/ApiKeys'
import CustomShortcodes from '@components/CustomShortcodes'
import EntityActivity from '@components/EntityActivity'
import EntityUsers from '@components/EntityUsers'
import LinkToEntityModule from '@components/LinkToEntityModule'
import MessagePanel from '@components/MessagePanel'
import ScopedConditionalContent from '@components/ScopedConditionalContent'
import TemplatePageList from '@components/TemplatePageList'

import AgencyBrandGroups from '../AgencyBrandGroups'
import AgencyDisabledBanner from '../../../../components/AgencyDisabledBanner'
import AgencyLockedBanner from '../../../../components/AgencyLockedBanner'
import AgencyOrderBundles from '../AgencyOrderBundles'
import AgencyPortals from '../AgencyPortals'
import AgencyProviderDeadlineGroups from '../AgencyProviderDeadlineGroups'
import AgencyProviderPackages from '../AgencyProviderPackages'
import AgencyProviders from '../AgencyProviders'
import AgencyPublications from '../AgencyPublications'
import AgencyTools from '../../../../components/AgencyTools'
import PageLayout from '../../../../components/PageLayout'

const PageContent = (props: { agencyId: number }) => {
  const { agencyId } = props

  const agencyPayload = useAgency({ id: agencyId })

  const { agency, brand, loading } = agencyPayload
  const { locked_by_user_id } = agency

  const {
    title: brandTitle,
    business_name: brandBusinessName,
    logo: brandLogo,
  } = brand || {}

  // @ts-expect-error TS2345
  const brandImageUrl = digObject(brandLogo, 'small', '')
  const { editBrandUrl } = generateUrls(brand)

  return (
    <PageLayout entityPayload={agencyPayload} activeTabBarItemKey="relations">
      <Columns.Main>
        <ConditionalContent visible={!!locked_by_user_id}>
          <AgencyLockedBanner agencyPayload={agencyPayload} />
        </ConditionalContent>

        <ScopedConditionalContent permittedRoleTitles={['Admin', 'Support']}>
          <ConditionalContent visible={!agency?.zuora_account_number}>
            <MessagePanel
              description={`This agency does not have a Zuora Account Number stored
                                        which means no invoices can be generated against this agency. If
                                        this agency should be invoiced for any reason, please have the
                                        agency added to Salesforce. Reach out to the Realhub Onboarding
                                        Specialist team to correctly link this account to Salesforce & Zuora.`}
              entityProperty
              marginBottom="large"
              title="Non Billable Agency"
            />
          </ConditionalContent>
        </ScopedConditionalContent>

        <ConditionalContent visible={!locked_by_user_id}>
          {!loading && <AgencyDisabledBanner agencyPayload={agencyPayload} />}

          {/*
           // @ts-expect-error TS2741 */}
          <ApiKeys
            filters={{ owner_id: agencyId, owner_type: 'Agency' }}
            userSearchRequestOptions={{ agency_id: agencyId }}
          />

          <AgencyOrderBundles agencyId={agencyId} />

          <ScopedConditionalContent permittedRoleTitles={['Admin', 'Support']}>
            <CustomShortcodes ownerId={agencyId} ownerType="Agency" />
          </ScopedConditionalContent>

          <AgencyProviderDeadlineGroups agencyId={agencyId} />

          <AgencyProviderPackages agencyId={agencyId} />

          <AgencyProviders agencyId={agencyId} />

          <AgencyPublications agencyId={agencyId} />

          <TemplatePageList
            allowedFilters={['service_product_id']}
            filterIds={{ agency_id: agencyId }}
          />

          <EntityUsers
            // @ts-expect-error TS2741
            filters={{ agency_id: agencyId }}
            initUser={{ association_id: agencyId, association_type: 'Agency' }}
            performHttpRequests={!!agencyId}
          />

          <ScopedConditionalContent permittedRoleTitles={['Admin', 'Support']}>
            <EntityActivity subjectId={agencyId} subjectType="Agency" />
          </ScopedConditionalContent>
        </ConditionalContent>
      </Columns.Main>

      <ConditionalContent visible={!locked_by_user_id}>
        <Columns.Sidebar>
          <AgencyTools agencyPayload={agencyPayload} />

          <ScopedConditionalContent permittedRoleTitles={['Admin', 'Support']}>
            <LinkToEntityModule
              entityTitle={brandTitle}
              goToHref={`${editBrandUrl}?redirect=${generateRedirectUrl()}`}
              imageUrl={brandImageUrl}
              loading={loading}
              moduleTitle="Brand"
              secondaryText={brandBusinessName}
            />

            <AgencyBrandGroups agencyId={agencyId} />

            <AgencyPortals agencyId={agencyId} />
          </ScopedConditionalContent>

        </Columns.Sidebar>
      </ConditionalContent>
    </PageLayout>
  )
}

export default PageContent
